/**
 * Country Detection Utility
 * Detect user's country for default filtering
 */

const COUNTRY_STORAGE_KEY = "vint_map_user_country";
const COUNTRY_EXPIRY_DAYS = 30;

interface StoredCountry {
    country: string;
    timestamp: number;
}

/**
 * Map browser language codes to country codes
 */
function languageToCountry(language: string): string {
    const lower = language.toLowerCase();
    const lang = lower.split('-')[0];

    const mapping: Record<string, string> = {
        'nl': 'NL',
        'fr': 'FR',
        'de': 'DE',
        'en': 'NL', // English defaults to NL
    };

    // Check for specific region codes
    if (lower.includes('-be')) return 'BE';
    if (lower.includes('-nl')) return 'NL';
    if (lower.includes('-fr')) return 'FR';
    if (lower.includes('-de')) return 'DE';

    // Default to NL unless it's explicitly one of our main supported countries
    const mapped = mapping[lang];
    if (mapped) return mapped;

    return 'NL';
}

/**
 * Get stored country from localStorage if not expired
 */
function getStoredCountry(): string | null {
    try {
        const stored = localStorage.getItem(COUNTRY_STORAGE_KEY);
        if (!stored) return null;

        const data: StoredCountry = JSON.parse(stored);
        const now = Date.now();
        const expiryMs = COUNTRY_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

        if (now - data.timestamp > expiryMs) {
            // Expired
            localStorage.removeItem(COUNTRY_STORAGE_KEY);
            return null;
        }

        return data.country;
    } catch (e) {
        return null;
    }
}

/**
 * Store country in localStorage
 */
function storeCountry(country: string): void {
    try {
        const data: StoredCountry = {
            country,
            timestamp: Date.now()
        };
        localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        // Ignore localStorage errors
    }
}

/**
 * Detect user's country based on browser language
 * Falls back to localStorage if available
 */
export function detectUserCountry(): string {
    // Check if we have a stored preference first
    const stored = getStoredCountry();
    const supportedCountries = ['NL', 'BE', 'FR', 'DE', 'ES'];

    if (stored) {
        // If the stored country is not supported, reset to NL
        if (!supportedCountries.includes(stored)) {
            console.log(`[CountryDetection] Unsupported country ${stored} detected, resetting to NL`);
            return 'NL';
        }
        return stored;
    }

    // Detect from browser language
    const language = navigator.language || (navigator as any).userLanguage || 'nl-NL';
    const detected = languageToCountry(language);

    // Ensure we return a supported country
    const finalCountry = supportedCountries.includes(detected) ? detected : 'NL';

    // Store for future visits
    storeCountry(finalCountry);

    return finalCountry;
}

/**
 * Manually set user's country preference
 */
export function setUserCountry(country: string): void {
    storeCountry(country);
    console.log(`[CountryDetection] User country manually set to: ${country}`);
}

/**
 * Clear stored country (for testing)
 */
export function clearStoredCountry(): void {
    localStorage.removeItem(COUNTRY_STORAGE_KEY);
}

/**
 * Get currency symbol for a given country code
 */
export function getCurrencyForCountry(countryCode: string): string {
    const code = countryCode.toUpperCase();

    // Eurozone + some others using Euro
    const euroCountries = [
        'NL', 'BE', 'DE', 'FR', 'ES', 'IT', 'PT', 'IE', 'AT', 'FI',
        'GR', 'LU', 'MT', 'CY', 'EE', 'LV', 'LT', 'SK', 'SI', 'HR', 'BG'
    ];

    if (euroCountries.includes(code)) return '€';
    if (code === 'UK' || code === 'GB') return '£';
    // US, CA and others default to $ as per requirement (or specific handling)
    // "rest van de wereld dollar teken"
    return '$';
}
